import { resolveColor, walk, type Element, type FrameElement, type Kind, type Screen, type Theme } from "./vocabulary.ts";

/**
 * Every edit the page editor can make, as a pure function from one screen to the next. The editor
 * keeps a history of screens, so undo is a pop, and posts diff(first, last) with the save.
 */

export type Change =
	| { kind: "changed"; element: string; property: string; old: unknown; new: unknown }
	| { kind: "reordered"; parent: string; order: string[] }
	| { kind: "moved"; element: string; from: string; to: string; index: number }
	| { kind: "deleted"; element: string }
	| { kind: "added"; element: string; parent: string; index: number; elementKind: Kind }
	| { kind: "theme"; property: string; old: unknown; new: unknown };

const DEFAULTS: { [K in Kind]: Omit<Extract<Element, { kind: K }>, "id"> } = {
	text: { kind: "text", text: "文字", size: 16, weight: 400, color: "text" },
	button: { kind: "button", label: "按钮", variant: "primary" },
	image: { kind: "image", src: "placeholder", width: 320, height: 180 },
	frame: { kind: "frame", layout: "column", gap: 8, width: "fill", children: [] },
};

interface Place {
	node: Element;
	parent: FrameElement | null;
	index: number;
}

function index(screen: Screen): Map<string, Place> {
	const out = new Map<string, Place>();
	for (const [node, parent] of walk(screen.root)) {
		out.set(node.id, { node, parent, index: parent ? parent.children!.indexOf(node) : 0 });
	}
	return out;
}

function clone(screen: Screen): Screen {
	return structuredClone(screen);
}

export function setProperty(screen: Screen, id: string, property: string, value: unknown): Screen {
	const next = clone(screen);
	const place = index(next).get(id);
	if (!place) return screen;
	const node = place.node as unknown as Record<string, unknown>;
	if (value === null || value === undefined) delete node[property];
	else node[property] = value;
	return next;
}

export function setTheme(screen: Screen, property: "font", value: Theme["font"]): Screen {
	const next = clone(screen);
	next.theme[property] = value;
	return next;
}

export function setThemeColor(screen: Screen, name: string, hex: string): Screen {
	const next = clone(screen);
	next.theme.colors[name] = hex.toUpperCase();
	return next;
}

/** The screen's width; the root frame follows it. */
export function setWidth(screen: Screen, width: number): Screen {
	const next = clone(screen);
	next.width = width;
	next.root.width = width;
	return next;
}

export function moveUp(screen: Screen, id: string): Screen {
	return shift(screen, id, -1);
}

export function moveDown(screen: Screen, id: string): Screen {
	return shift(screen, id, 1);
}

function shift(screen: Screen, id: string, by: number): Screen {
	const next = clone(screen);
	const place = index(next).get(id);
	if (!place?.parent) return screen;
	const siblings = place.parent.children!;
	const to = place.index + by;
	if (to < 0 || to >= siblings.length) return screen;
	siblings.splice(place.index, 1);
	siblings.splice(to, 0, place.node);
	return next;
}

/** Drops the subtree; nextId stays, since ids are never reused. */
export function remove(screen: Screen, id: string): Screen {
	const next = clone(screen);
	const place = index(next).get(id);
	if (!place?.parent) return screen;
	place.parent.children!.splice(place.index, 1);
	return next;
}

/** A new element of `kind` under `parentId` at `index` (clamped), with the next id. */
export function add(screen: Screen, parentId: string, at: number, kind: Kind): Screen {
	const next = clone(screen);
	const place = index(next).get(parentId);
	if (!place || place.node.kind !== "frame") return screen;
	const parent = place.node;
	const element = { id: `e${next.nextId}`, ...structuredClone(DEFAULTS[kind]) } as Element;
	next.nextId += 1;
	parent.children ??= [];
	parent.children.splice(Math.max(0, Math.min(at, parent.children.length)), 0, element);
	return next;
}

/** Appends `id` to the frame `frameId`; refuses the root, a non-frame target, and its own descendant. */
export function moveInto(screen: Screen, id: string, frameId: string): Screen {
	const next = clone(screen);
	const places = index(next);
	const place = places.get(id);
	const target = places.get(frameId);
	if (!place?.parent || !target || target.node.kind !== "frame") return screen;
	for (const [node] of walk(place.node)) if (node.id === frameId) return screen;
	place.parent.children!.splice(place.index, 1);
	(target.node.children ??= []).push(place.node);
	return next;
}

/** What changed between two screens, in the kinds figma_diff.py uses plus added and theme. */
export function diff(first: Screen, last: Screen): Change[] {
	const changes: Change[] = [];
	for (const property of ["font", "width"] as const) {
		const a = property === "font" ? first.theme.font : first.width;
		const b = property === "font" ? last.theme.font : last.width;
		if (a !== b) changes.push({ kind: "theme", property, old: a, new: b });
	}
	for (const name of new Set([...Object.keys(first.theme.colors), ...Object.keys(last.theme.colors)])) {
		const a = first.theme.colors[name] ?? null;
		const b = last.theme.colors[name] ?? null;
		if (a !== b) changes.push({ kind: "theme", property: `colors.${name}`, old: a, new: b });
	}
	// Theme changes sort by kind of thing, not by the order the loops above happen to run.
	changes.sort((x, y) => themeOrder(x) - themeOrder(y));

	const before = index(first);
	const after = index(last);
	for (const [id, was] of before) {
		const now = after.get(id);
		if (!now) {
			changes.push({ kind: "deleted", element: id });
			continue;
		}
		for (const property of propertiesOf(was.node, now.node)) {
			const a = (was.node as Record<string, unknown>)[property] ?? null;
			const b = (now.node as Record<string, unknown>)[property] ?? null;
			if (!same(a, b)) changes.push({ kind: "changed", element: id, property, old: a, new: b });
		}
		if (was.parent && now.parent && was.parent.id !== now.parent.id) {
			changes.push({ kind: "moved", element: id, from: was.parent.id, to: now.parent.id, index: now.index });
		}
	}
	for (const [node] of walk(last.root)) {
		if (node.kind !== "frame" || !before.has(node.id)) continue;
		const wasChildren = ((before.get(node.id)!.node as FrameElement).children ?? []).map((c) => c.id);
		const nowChildren = (node.children ?? []).map((c) => c.id);
		const shared = (ids: string[]) => ids.filter((id) => wasChildren.includes(id) && nowChildren.includes(id) && before.get(id)!.parent?.id === node.id && after.get(id)!.parent?.id === node.id);
		if (shared(wasChildren).join() !== shared(nowChildren).join()) changes.push({ kind: "reordered", parent: node.id, order: nowChildren });
	}
	for (const [node, parent] of walk(last.root)) {
		if (!before.has(node.id) && parent) {
			changes.push({ kind: "added", element: node.id, parent: parent.id, index: after.get(node.id)!.index, elementKind: node.kind });
		}
	}
	return changes;
}

function themeOrder(change: Change): number {
	if (change.kind !== "theme") return 0;
	return change.property === "font" ? 0 : change.property.startsWith("colors.") ? 1 : 2;
}

function propertiesOf(a: Element, b: Element): string[] {
	const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
	keys.delete("id");
	keys.delete("kind");
	keys.delete("children");
	return [...keys].sort();
}

function same(a: unknown, b: unknown): boolean {
	return JSON.stringify(a) === JSON.stringify(b);
}

export { resolveColor };
